import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  Spinner,
  Table,
  Progress,
} from "reactstrap";
import axios from "axios";
import classnames from "classnames";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useTable, useSortBy } from "react-table";
import { useParams } from "react-router-dom";
import SbomReport from "./sbomreport";
import SummaryTab from "./summarytab";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import VulnerabilitySummary from "./tablesbom";
import logopath from "../../../assets/images/logo-light.png";

const generateRandomData = (jsonData) => {
  let flattenedData = [];

  Object.keys(jsonData.vulnerabilities).forEach((hostKey) => {
    const hostVulnerabilities = jsonData.vulnerabilities[hostKey];

    hostVulnerabilities.forEach((vulnerability) => {
      flattenedData.push({
        risk: vulnerability.risk_factor,
        host: hostKey,
        port: vulnerability.port,
        name: vulnerability.name,
        synopsis: vulnerability.synopsis,
        description: vulnerability.description,
        solution: vulnerability.solution,
      });
    });
  });

  return flattenedData;
};

const calculateRiskDistribution = (scanData) => {
  const riskLevels = ["High", "Medium", "Low", "None"];
  const riskDistribution = {};

  scanData.forEach((row) => {
    if (!riskDistribution[row.host]) {
      riskDistribution[row.host] = {
        High: 0,
        Medium: 0,
        Low: 0,
        None: 0,
        Null: 0,
      };
    }

    const risk = row.risk === null ? "Null" : row.risk;
    riskDistribution[row.host][risk] += 1;
  });

  Object.keys(riskDistribution).forEach((host) => {
    const total = Object.values(riskDistribution[host]).reduce((a, b) => a + b, 0);
    riskDistribution[host] = {
      ...riskDistribution[host],
      total,
    };
  });

  return riskDistribution;
};

const fetchScanResults = async (userId, scanId) => {
  try {
    const response = await axios.get(
      `https://api.bluhawk.live/api/vulnerability-scan/${userId}/results/${scanId}`
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    console.error("Error fetching scan results:", error);
    throw error;
  }
};

const ScanResults = () => {
  document.title = "Scan Results";
  const { scanId } = useParams();
  const [scanName, setScanName] = useState("");
  const [scanType, setScanType] = useState("");
  const [activeTab, setActiveTab] = useState("summary");
  const [scanData, setScanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [jsonData, setJsonData] = useState(null);



  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;
  const [isExporting, setIsExporting] = useState(false);


  const getRiskColor = (risk) => {
    switch (risk) {
      case "High":
        return "text-danger fw-bold";
      case "Medium":
        return "text-warning fw-bold";
      case "Low":
        return "text-success fw-bold";
      case "None":
        return "text-primary fw-bold";
      default:
        return "text-secondary fw-bold";
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Constants
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      const usableWidth = pageWidth - (2 * margin);

      // Brand Colors (Dark Theme)
      const colors = {
        background: [22, 28, 36],
        cardBg: [32, 37, 45],
        accent: [89, 205, 255],
        text: {
          primary: [255, 255, 255],
          secondary: [200, 200, 200],
          muted: [109, 109, 109]
        },
        risk: {
          critical: [220, 53, 69],
          high: [220, 53, 69],
          medium: [255, 193, 7],
          low: [40, 167, 69],
          none: [0, 123, 255]
        }
      };

      const setupPage = (pageTitle = '') => {
        pdf.setFillColor(...colors.background);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');

        if (pageTitle) {
          pdf.setFillColor(...colors.cardBg);
          pdf.roundedRect(margin, margin, usableWidth, 12, 2, 2, 'F');

          pdf.setFontSize(12);
          pdf.setTextColor(...colors.text.primary);
          pdf.text(pageTitle, margin + 5, margin + 8);
        }

        pdf.setDrawColor(...colors.accent);
        pdf.setLineWidth(0.5);
        pdf.rect(margin / 2, margin / 2, pageWidth - margin, pageHeight - margin, 'S');
      };

      // Cover Page
      setupPage();

      // Logo (top right)
      pdf.addImage(logopath, 'PNG', pageWidth - margin - 45, margin, 45, 15);

      pdf.setFontSize(28);
      pdf.setTextColor(...colors.text.primary);
      const title = 'Vulnerability Assessment Report';
      const titleWidth = pdf.getStringUnitWidth(title) * 28 / pdf.internal.scaleFactor;
      pdf.text(title, (pageWidth - titleWidth) / 2, 60);

      // Enhanced Professional Metadata Box
      pdf.setFillColor(...colors.cardBg);
      pdf.roundedRect(margin, 80, usableWidth, 50, 2, 2, 'F');

      pdf.setFontSize(10);
      pdf.setTextColor(...colors.text.secondary);
      const formatDate = (date) => {
        const day = date.getDate();
        const month = date.toLocaleString('en-US', { month: 'long' });
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const timeZone = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ')[2];
        
        // Function to get the ordinal suffix
        const getOrdinalSuffix = (n) => {
          const s = ["th", "st", "nd", "rd"];
          const v = n % 100;
          return s[(v - 20) % 10] || s[v] || s[0];
        };
      
        return `${day}${getOrdinalSuffix(day)} ${month} ${year}, ${hours}.${minutes} ${timeZone}`;
      };
      
      const metadata = [
        ['Scan ID:', scanId],
        ['Scan Name:', scanName],
        ['Report Generated:', formatDate(new Date())],
        
      ];

      metadata.forEach((detail, index) => {
        pdf.text(detail[0], margin + 5, 90 + (index * 10));
        pdf.setTextColor(...colors.text.primary);
        pdf.text(detail[1], margin + 45, 90 + (index * 10));
        pdf.setTextColor(...colors.text.secondary);
      });

      // Enhanced Executive Summary
      pdf.setFillColor(...colors.cardBg);
      pdf.roundedRect(margin, 140, usableWidth, 80, 2, 2, 'F');

      pdf.setFontSize(14);
      pdf.setTextColor(...colors.text.primary);
      pdf.text('Executive Summary', margin + 5, 150);

      pdf.setFontSize(10);
      pdf.setTextColor(...colors.text.secondary);
      const executiveSummary = [
        'This vulnerability assessment report provides a comprehensive analysis of the security posture',
        'of the scanned systems. The assessment identifies potential security weaknesses, evaluates their',
        'severity, and provides actionable remediation steps.',
        '',
        'Key Findings:',
        `• ${calculateHighSeverityCount(scanData)} high-severity vulnerabilities requiring immediate attention`,
        `• ${calculateMediumSeverityCount(scanData)} medium-severity issues that should be addressed in the near term`,
        `• ${calculatePatchableCount(scanData)}% of identified vulnerabilities can be resolved through patching`,
        '',
        'Recommendations are prioritized based on risk levels and potential business impact.'
      ];

      executiveSummary.forEach((line, index) => {
        pdf.text(line, margin + 5, 160 + (index * 5));
      });

      // Enhanced Disclaimer
      pdf.setFillColor(...colors.cardBg);
      pdf.roundedRect(margin, 230, usableWidth, 45, 2, 2, 'F');

      pdf.setFontSize(11);
      pdf.setTextColor(...colors.accent);
      pdf.text('CONFIDENTIAL DOCUMENT', margin + 5, 240);

      pdf.setFontSize(9);
      pdf.setTextColor(...colors.text.secondary);
      const disclaimer = [
        'This vulnerability assessment report is valid for 3 months from the date of generation.',
        'The findings represent the system state at the time of scanning and may not reflect current security status.',
        'Regular reassessment is recommended to maintain security posture.',
        'Implementation of the provided remediation steps should be validated through follow-up testing.'
      ];

      disclaimer.forEach((line, index) => {
        pdf.text(line, margin + 5, 250 + (index * 6));
      });

      // Total Summary Page
      pdf.addPage();
      setupPage('Total Summary');

      // Add total counts
      pdf.setFillColor(...colors.cardBg);
      pdf.roundedRect(margin, 40, usableWidth, 25, 2, 2, 'F');

      pdf.setTextColor(...colors.text.primary);
      pdf.setFontSize(11);
      pdf.text(`Total Hosts: ${Object.keys(riskDistribution).length}`, margin + 10, 50);
      pdf.text(`Total Vulnerabilities: ${scanData.length}`, margin + 100, 50);
      

      const riskSummary = calculateRiskSummary(scanData);
      let yPosition = 80;

      // Risk Distribution Cards with improved layout
      Object.entries(riskSummary).forEach(([risk, count]) => {
        pdf.setFillColor(...colors.cardBg);
        pdf.roundedRect(margin, yPosition, usableWidth, 15, 2, 2, 'F');

        // Shield icon instead of dot
        const shieldPath = `M${margin + 10},${yPosition + 7.5} l3,-3 l3,3 l0,4 l-3,2 l-3,-2 z`;
        pdf.setFillColor(...(colors.risk[risk.toLowerCase()] || colors.risk.none));
        pdf.path(shieldPath, 'F');

        // Risk details with tighter spacing
        pdf.setTextColor(...colors.text.primary);
        pdf.text(`${risk}`, margin + 25, yPosition + 10);
        pdf.text(`${count}`, margin + 60, yPosition + 10);

        // Compact progress bar
        const percentage = (count / scanData.length) * 100;
        pdf.setFillColor(40, 40, 40);
        pdf.roundedRect(margin + 80, yPosition + 5, 40, 4, 1, 1, 'F');

        if (percentage > 0) {
          pdf.setFillColor(...(colors.risk[risk.toLowerCase()] || colors.risk.none));
          pdf.roundedRect(margin + 80, yPosition + 5, (percentage * 40 / 100), 4, 1, 1, 'F');
        }

        yPosition += 20;
      });

      // Host-specific Findings
      Object.entries(riskDistribution).forEach((hostEntry) => {
        const [host, distribution] = hostEntry;

        pdf.addPage();
        setupPage(`Host Details: ${host}`);

        // Slimmer Host Risk Distribution
        let currentY = 40;

        pdf.setFillColor(...colors.cardBg);
        pdf.roundedRect(margin, currentY, usableWidth, 25, 2, 2, 'F');

        const total = distribution.total || 1;
        let xOffset = margin + 10;

        // Progress bar without text
        Object.entries(distribution).forEach(([risk, count]) => {
          if (risk !== 'total') {
            const width = (count / total) * (usableWidth - 20);
            pdf.setFillColor(...(colors.risk[risk.toLowerCase()] || colors.risk.none));
            pdf.rect(xOffset, currentY + 8, width, 8, 'F');
            xOffset += width;
          }
        });

        currentY += 35;

        // Vulnerabilities Table with proper text wrapping
        const hostVulns = scanData.filter(v => v.host === host);

        const columns = [
          { header: 'Risk', width: 25 },
          { header: 'Port', width: 25 },
          { header: 'Name', width: 45 },
          { header: 'Solution', width: usableWidth - 95 }
        ];

        // Table headers
        pdf.setFillColor(...colors.cardBg);
        pdf.rect(margin, currentY, usableWidth, 10, 'F');

        let xPos = margin + 2;
        columns.forEach(col => {
          pdf.setTextColor(...colors.text.primary);
          pdf.setFontSize(9);
          pdf.text(col.header, xPos, currentY + 7);
          xPos += col.width;
        });

        currentY += 12;

        // Table rows with text wrapping
        hostVulns.forEach((vuln, index) => {
          if (currentY > pageHeight - 30) {
            pdf.addPage();
            setupPage(`Host Details: ${host} (continued)`);
            currentY = 40;
          }

          const lineHeight = 8;
          const cellPadding = 2;

          // Calculate wrapped text heights
          const nameLines = pdf.splitTextToSize(vuln.name || '', columns[2].width - 4);
          const solutionLines = pdf.splitTextToSize(vuln.solution || '', columns[3].width - 4);
          const rowHeight = Math.max(nameLines.length, solutionLines.length) * lineHeight + (2 * cellPadding);

          // Row background
          pdf.setFillColor(...(index % 2 === 0 ? colors.cardBg : [40, 45, 53]));
          pdf.rect(margin, currentY, usableWidth, rowHeight, 'F');

          // Cell content
          xPos = margin + 2;
          pdf.setTextColor(...colors.text.primary);
          pdf.setFontSize(8);

          // Risk
          pdf.setFillColor(...(colors.risk[vuln.risk?.toLowerCase()] || colors.risk.none));
          pdf.rect(xPos + 2, currentY + (rowHeight / 2) - 2, 4, 4, 'F');
          pdf.text(vuln.risk || 'N/A', xPos + 8, currentY + (rowHeight / 2) + 2);

          // Port
          xPos += columns[0].width;
          pdf.text(vuln.port?.toString() || 'N/A', xPos, currentY + (rowHeight / 2) + 2);

          // Name
          xPos += columns[1].width;
          nameLines.forEach((line, i) => {
            pdf.text(line, xPos, currentY + cellPadding + (i + 1) * lineHeight);
          });

          // Solution
          xPos += columns[2].width;
          solutionLines.forEach((line, i) => {
            pdf.text(line, xPos, currentY + cellPadding + (i + 1) * lineHeight);
          });

          currentY += rowHeight;
        });
        
      });

      // Page numbers
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(...colors.text.muted);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - 25, pageHeight - 10);
      }

      pdf.save(`${scanName}_Vulnerability_Assessment_Report.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const calculateHighSeverityCount = (data) => {
    return data.filter(vuln => vuln.risk === 'High').length;
  };

  const calculateMediumSeverityCount = (data) => {
    return data.filter(vuln => vuln.risk === 'Medium').length;
  };

  const calculatePatchableCount = (data) => {
    const patchable = data.filter(vuln =>
      vuln.solution && vuln.solution.toLowerCase().includes('update') ||
      vuln.solution.toLowerCase().includes('patch') ||
      vuln.solution.toLowerCase().includes('upgrade')
    ).length;
    return Math.round((patchable / data.length) * 100);
  };

  // Helper function to calculate risk summary
  const calculateRiskSummary = (scanData) => {
    return scanData.reduce((acc, vuln) => {
      const risk = vuln.risk || 'None';
      acc[risk] = (acc[risk] || 0) + 1;
      return acc;
    }, {});
  };
  // Helper function to get risk colors
  const getRiskColornew = (risk) => {
    const colors = {
      Critical: [168, 12, 12],
      High: [220, 53, 69],
      Medium: [255, 152, 0],
      Low: [40, 167, 69],
      None: [0, 123, 255],
      default: [108, 117, 125]
    };
    return colors[risk] || colors.default;
  };

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const jsonData = await fetchScanResults(userId, scanId);
        setScanName(jsonData.Basic.Name);
        setScanType(jsonData.mainscanType || "");
        setJsonData(jsonData);

        if (jsonData.mainscanType !== "SBOM") {
          const data = generateRandomData(jsonData);
          setScanData(data);
        }
        setLoading(false);
      } catch (err) {
        setError("Analysis in Progress");
        setLoading(false);
      }
    };
    fetchResults();
  }, [userId, scanId]);

  const riskDistribution = calculateRiskDistribution(scanData);

  const columns = React.useMemo(
    () => [
      { Header: "Host", accessor: "host" },
      {
        Header: "Risk", accessor: "risk", Cell: ({ value }) => (
          <span className={getRiskColor(value)}>{value || "N/A"}</span>
        ),
      },
      { Header: "Port", accessor: "port" },
      { Header: "Name", accessor: "name" },
      { Header: "Synopsis", accessor: "synopsis" },
      { Header: "Description", accessor: "description" },
      { Header: "Solution", accessor: "solution" },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: scanData,
        initialState: { sortBy: [{ id: "risk", desc: false }] },
      },
      useSortBy
    );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={scanName} breadcrumbItem={scanId} />
          {(loading || error) && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                minHeight: "100vh",
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-10%, -50%)",
              }}
            >
              <div className="text-center">
                <h4>{loading ? "Loading scan results..." : error}</h4>
                <Spinner
                  color="primary"
                  style={{
                    width: "3rem",
                    height: "3rem",
                    animationDuration: "1.5s",
                    borderWidth: "0.5rem",
                  }}
                />
              </div>
            </div>
          )}

          {!loading && !error && scanType === "SBOM" ? (
            <VulnerabilitySummary data={jsonData} />

          ) : (
            !loading &&
            !error && (
              <Row>
                <Col sm="12">
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center">
                        <h4 className="card-title">Scan Results for {scanName}</h4>
                        <Button
                          color="primary"
                          onClick={exportToPDF}
                          disabled={isExporting}
                          className="mb-3"
                        >
                          {isExporting ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              <span>Generating Report...</span>
                            </>
                          ) : (
                            "Export Report"
                          )}
                        </Button>
                      </div>
                      <div id="scan-results-content">
                        <p className="text-muted">
                          Scan Date: {new Date().toLocaleDateString()}
                        </p>
                        <Nav tabs className="nav-tabs-custom">
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: activeTab === "summary",
                              })}
                              onClick={() => setActiveTab("summary")}
                            >
                              Summary
                            </NavLink>
                          </NavItem>
                          {/* <NavItem>
                            <NavLink
                              className={classnames({
                                active: activeTab === "hosts",
                              })}
                              onClick={() => setActiveTab("hosts")}
                            >
                              Hosts
                            </NavLink>
                          </NavItem> */}
                          <NavItem>
                            <NavLink
                              className={classnames({
                                active: activeTab === "vulnerabilities",
                              })}
                              onClick={() => setActiveTab("vulnerabilities")}
                            >
                              Vulnerabilities
                            </NavLink>
                          </NavItem>
                        </Nav>
                        <TabContent activeTab={activeTab}>
                          <TabPane tabId="summary">
                            <Card>
                              {Object.keys(riskDistribution).map((host) => {
                                const distribution = riskDistribution[host];
                                const total = distribution.total || 1;
                                return (
                                  <div key={host} className="mt-3">
                                    <br />
                                    <h6>Risk Distribution: {host} </h6>
                                    <Progress multi style={{ height: "1.5rem" }}>
                                      <Progress
                                        bar
                                        color="success"
                                        value={(distribution.Low / total) * 100}
                                      >
                                        {distribution.Low}
                                      </Progress>
                                      <Progress
                                        bar
                                        color="warning"
                                        value={
                                          (distribution.Medium / total) * 100
                                        }
                                      >
                                        {distribution.Medium}
                                      </Progress>
                                      <Progress
                                        bar
                                        color="danger"
                                        value={(distribution.High / total) * 100}
                                      >
                                        {distribution.High}
                                      </Progress>
                                      <Progress
                                        bar
                                        color="primary"
                                        value={(distribution.None / total) * 100}
                                      >
                                        {distribution.None}
                                      </Progress>
                                      <Progress
                                        bar
                                        color="secondary"
                                        value={(distribution.Null / total) * 100}
                                      >
                                        {distribution.Null}
                                      </Progress>
                                    </Progress>
                                  </div>
                                );
                              })}

                            </Card>
                            <hr />
                            <SummaryTab scanData={scanData} />
                          </TabPane>
                          {/* <TabPane tabId="hosts">
                            {Object.keys(riskDistribution).map((host) => {
                              const distribution = riskDistribution[host];
                              const total = distribution.total || 1;
                              return (
                                <div key={host} className="mt-3">
                                  <h6>{host} Risk Distribution</h6>
                                  <Progress multi style={{ height: "1.5rem" }}>
                                    <Progress
                                      bar
                                      color="success"
                                      value={(distribution.Low / total) * 100}
                                    >
                                      {distribution.Low}
                                    </Progress>
                                    <Progress
                                      bar
                                      color="warning"
                                      value={
                                        (distribution.Medium / total) * 100
                                      }
                                    >
                                      {distribution.Medium}
                                    </Progress>
                                    <Progress
                                      bar
                                      color="danger"
                                      value={(distribution.High / total) * 100}
                                    >
                                      {distribution.High}
                                    </Progress>
                                    <Progress
                                      bar
                                      color="primary"
                                      value={(distribution.None / total) * 100}
                                    >
                                      {distribution.None}
                                    </Progress>
                                    <Progress
                                      bar
                                      color="secondary"
                                      value={(distribution.Null / total) * 100}
                                    >
                                      {distribution.Null}
                                    </Progress>
                                  </Progress>
                                </div>
                              );
                            })}
                          </TabPane> */}
                          <TabPane tabId="vulnerabilities">
                            <Card>
                              <CardBody >
                                <h5 className="font-weight-semibold">
                                  Vulnerabilities
                                </h5>
                                <Table {...getTableProps()}>
                                  <thead>
                                    {headerGroups.map((headerGroup) => (
                                      <tr
                                        {...headerGroup.getHeaderGroupProps()}
                                        key={headerGroup.id}
                                      >
                                        {headerGroup.headers.map((column) => (
                                          <th
                                            {...column.getHeaderProps(
                                              column.getSortByToggleProps()
                                            )}
                                            key={column.id}
                                          >
                                            {column.render("Header")}

                                          </th>
                                        ))}
                                      </tr>
                                    ))}
                                  </thead>
                                  <tbody {...getTableBodyProps()}>
                                    {rows.map((row) => {
                                      prepareRow(row);
                                      return (
                                        <tr {...row.getRowProps()} key={row.id}>
                                          {row.cells.map((cell) => (
                                            <td {...cell.getCellProps()} key={cell.id} style={{ textAlign: "justify" }}>
                                              {cell.render("Cell")}

                                            </td>
                                          ))}
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </Table>
                              </CardBody>
                            </Card>
                          </TabPane>
                        </TabContent>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            )
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ScanResults;
